/**
 * 客製化元件模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from "react";
import get from 'lodash/get';
import { useRecordContext, SaveButton, EditButton, Toolbar, ReferenceField, TextField, SelectInput, ReferenceInput } from 'react-admin';
import { useWatch } from 'react-hook-form';

//-----------------------------------------------------------------------
// defines
import { ANALYTICS_TIME_RANGE, MEMBER_LOG_TYPE } from "../common/defines";
import { NamePairInfo } from "../common/interfaces";

/** 操作紀錄種類整數欄位 1 定義。 */
enum MEMBER_LOG_INT1_TYPE
{
	MemberId,
	SourceId,
	KeywordId,
	ManuallyChannelId,
	KolId,
	ScheduleJobId,
	ChannelId,
	TargetId
}

/** 操作紀錄種類欄位資訊。 */
interface MemberLogTypeInfo
{
	id			: MEMBER_LOG_TYPE
	name		: string
	int1Type	: MEMBER_LOG_INT1_TYPE
	isModify	: boolean
}

/** 操作紀錄修改資料資訊。 */
interface MemberLogModifyInfo
{
	f			: string,
	b			: any,
	a			: any
}

//-----------------------------------------------------------------------
// 共用定義。

/** 時間範圍選項列表。 */
export const timeRangeList : NamePairInfo<string>[] =
[
	{ id: ANALYTICS_TIME_RANGE.Monthly,		name: "月份" },
	{ id: ANALYTICS_TIME_RANGE.Quarterly,	name: "季度" },
	{ id: ANALYTICS_TIME_RANGE.Yearly,		name: "年度" },
];

//-----------------------------------------------------------------------
const quarterList : NamePairInfo<number>[] = [];
const monthList : NamePairInfo<number>[] = [];

for(let quarter = 1; quarter <= 4; quarter++)
	quarterList.push({ id: quarter, name: `第 ${quarter} 季`})
for(let month = 1; month <= 12; month++)
	monthList.push({ id: month, name: `${month} 月` });

export const TimeRangeInput = (props: any) =>
{
	const timeRange = useWatch({ name: 'timeRange' });
	const yearList : NamePairInfo<number>[] = [];
	const now = new Date();

	for(let year = 2022; year <= now.getFullYear(); year++)
		yearList.push({ id: year, name: year.toString() });

	if(timeRange === ANALYTICS_TIME_RANGE.Monthly)
	{
		return <><SelectInput label='時間範圍' source='timeRange' defaultValue={ANALYTICS_TIME_RANGE.Monthly} choices={timeRangeList} /><SelectInput label='年度' source='year' choices={yearList} /><SelectInput label='月份' source='month' choices={monthList} /></>;
	}
	else if(timeRange === ANALYTICS_TIME_RANGE.Quarterly)
	{
		return <><SelectInput label='時間範圍' source='timeRange' defaultValue={ANALYTICS_TIME_RANGE.Monthly} choices={timeRangeList} /><SelectInput label='年度' source='year' choices={yearList} /><SelectInput label='季度' source='quarter' choices={quarterList} /></>;
	}
	else if(timeRange === ANALYTICS_TIME_RANGE.Yearly)
	{
		return <><SelectInput label='時間範圍' source='timeRange' defaultValue={ANALYTICS_TIME_RANGE.Monthly} choices={timeRangeList} /><SelectInput label='年度' source='year' choices={yearList} /></>;
	}

	return <SelectInput label='時間範圍' source='timeRange' defaultValue={ANALYTICS_TIME_RANGE.Monthly} choices={timeRangeList} />;
}

//-----------------------------------------------------------------------
export interface ScheduleKeywordFilterProps
{
	onChanged	: (keywordId: number) => void
}

export const ScheduleKeywordFilter = (props: ScheduleKeywordFilterProps) =>
{
	const keywordId = useWatch({ name: 'keywordId' });

	props.onChanged(keywordId);

	return (
		<ReferenceInput label='關鍵字' source='keywordId' reference='keywordAll' alwaysOn >
			<SelectInput optionText='text' />
		</ReferenceInput>);
}

//-----------------------------------------------------------------------
/** 只有儲存按鈕的編輯頁面工具列。 */
export const SaveOnlyToolbar = (props: any) => (
    <Toolbar {...props} >
        <SaveButton disabled={props.pristine} />
    </Toolbar>
);

//-----------------------------------------------------------------------
/** 以文字來源替代網址的 UrlField。 */
export const TextUrlField = (props: any) =>
{
	const record = useRecordContext(props);

	if (!record) return <></>;

	return <a href={record[props.source]} {...props} >{get(record, props.textSource)}</a>;
}

//-----------------------------------------------------------------------
/** 加入權限判斷的列表編輯按鈕。 */
export const ListEditButton = (props: any) =>
{
	if(props.edit)
		return <EditButton {...props} />

	return <span/>;
}

//-----------------------------------------------------------------------
/** 操作紀錄種類顯示欄位元件。 */

const memberLogTypeMap = new Map<MEMBER_LOG_TYPE, MemberLogTypeInfo>();
const memberLogTypeList : MemberLogTypeInfo[] =
[
	{ id: MEMBER_LOG_TYPE.MemberLogin,				name: "管理員登入",			int1Type: MEMBER_LOG_INT1_TYPE.MemberId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.MemberLogout,				name: "管理員登出",			int1Type: MEMBER_LOG_INT1_TYPE.MemberId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.MemberCreate,				name: "建立管理員",			int1Type: MEMBER_LOG_INT1_TYPE.MemberId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.MemberModify,				name: "編輯管理員",			int1Type: MEMBER_LOG_INT1_TYPE.MemberId,			isModify: true },
	{ id: MEMBER_LOG_TYPE.MemberDelete,				name: "刪除管理員",			int1Type: MEMBER_LOG_INT1_TYPE.MemberId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.SourceCreate,				name: "建立資料來源",		int1Type: MEMBER_LOG_INT1_TYPE.SourceId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.SourceModify,				name: "編輯資料來源",		int1Type: MEMBER_LOG_INT1_TYPE.SourceId,			isModify: true },
	{ id: MEMBER_LOG_TYPE.KeywordCreate,			name: "建立關鍵字",			int1Type: MEMBER_LOG_INT1_TYPE.KeywordId,			isModify: false },
	{ id: MEMBER_LOG_TYPE.KeywordModify,			name: "編輯關鍵字",			int1Type: MEMBER_LOG_INT1_TYPE.KeywordId,			isModify: true },
	{ id: MEMBER_LOG_TYPE.ManuallyChannelCreate,	name: "建立手動頻道",		int1Type: MEMBER_LOG_INT1_TYPE.ManuallyChannelId,	isModify: false },
	{ id: MEMBER_LOG_TYPE.ManuallyChannelModify,	name: "編輯手動頻道",		int1Type: MEMBER_LOG_INT1_TYPE.ManuallyChannelId,	isModify: true },
	{ id: MEMBER_LOG_TYPE.KolCreate,				name: "建立 KOL",			int1Type: MEMBER_LOG_INT1_TYPE.KolId,				isModify: false },
	{ id: MEMBER_LOG_TYPE.KolModify,				name: "編輯 KOL",			int1Type: MEMBER_LOG_INT1_TYPE.KolId,				isModify: true },
	{ id: MEMBER_LOG_TYPE.ScheduleCreate,		name: "建立排程工作",		int1Type: MEMBER_LOG_INT1_TYPE.ScheduleJobId,		isModify: false },
	{ id: MEMBER_LOG_TYPE.ScheduleModify,		name: "編輯排程工作",		int1Type: MEMBER_LOG_INT1_TYPE.ScheduleJobId,		isModify: true },
	{ id: MEMBER_LOG_TYPE.ChannelModify,			name: "編輯頻道",			int1Type: MEMBER_LOG_INT1_TYPE.ChannelId,			isModify: true },
	{ id: MEMBER_LOG_TYPE.TargetModify,				name: "編輯目標",			int1Type: MEMBER_LOG_INT1_TYPE.TargetId,			isModify: true }
];

for(let type of memberLogTypeList)
	memberLogTypeMap.set(type.id, type);

export const MemberLogTypeField = (props: any) =>
{
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{memberLogTypeMap.get(get(record, 'type'))?.name}</span>);
}

MemberLogTypeField.defaultProps =
{
	label: '操作紀錄種類',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 操作紀錄資料顯示欄位元件。 */

const memberLogModifyFieldMap = new Map<string, string>();

memberLogModifyFieldMap.set('status', '狀態');
memberLogModifyFieldMap.set('group', '群組');
memberLogModifyFieldMap.set('name', '名稱');
memberLogModifyFieldMap.set('email', 'e-mail');
memberLogModifyFieldMap.set('url', '網址');
memberLogModifyFieldMap.set('text', '文字內容');
memberLogModifyFieldMap.set('limit', '上限');
memberLogModifyFieldMap.set('sourceId', '資料來源');
memberLogModifyFieldMap.set('keywordId', '關鍵字');
memberLogModifyFieldMap.set('keywords', '關鍵字');
memberLogModifyFieldMap.set('job', '工作');
memberLogModifyFieldMap.set('parameters', '參數');
memberLogModifyFieldMap.set('time', '時間');

export const MemberLogDataField = (props: any) =>
{
	const record = useRecordContext(props);
	const typeInfo = memberLogTypeMap.get(get(record, 'type'));
	let	dataValue = get(record, 'data');

	if(typeInfo?.isModify)
	{
		try
		{
			let	modifyInfo = JSON.parse(dataValue) as MemberLogModifyInfo;
			let	elmTarget : React.ReactElement;

			if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.MemberId)
			{
				elmTarget = <ReferenceField source="int1" reference="member">
						<TextField source="name" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.SourceId)
			{
				elmTarget = <ReferenceField source="int1" reference="source">
						<TextField source="name" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.KeywordId)
			{
				elmTarget = <ReferenceField source="int1" reference="keyword">
						<TextField source="text" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.ManuallyChannelId)
			{
				elmTarget = <ReferenceField source="int1" reference="manuallyChannel">
						<TextField source="id" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.KolId)
			{
				elmTarget = <ReferenceField source="int1" reference="kol">
						<TextField source="name" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.ScheduleJobId)
			{
				elmTarget = <ReferenceField source="int1" reference="schedule">
						<TextField source="id" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.ChannelId)
			{
				elmTarget = <ReferenceField source="int1" reference="channel">
						<TextField source="name" />
					</ReferenceField>;
			}
			else if(typeInfo?.int1Type === MEMBER_LOG_INT1_TYPE.TargetId)
			{
				elmTarget = <ReferenceField source="int1" reference="target">
						<TextField source="title" />
					</ReferenceField>;
			}
			else
				elmTarget = <span/>;

			if(modifyInfo.f === 'passcode')
				return <span className="MuiTypography-body2 MuiTypography-root">{elmTarget} 變更登入驗證碼。</span>;
			else if(memberLogModifyFieldMap.has(modifyInfo.f))
			{
				return <span className="MuiTypography-body2 MuiTypography-root">將 {elmTarget} 的 {`${memberLogModifyFieldMap.get(modifyInfo.f)} 由 ${modifyInfo.b} 變更為 ${modifyInfo.a}。`}</span>;
			}

			return <span className="MuiTypography-body2 MuiTypography-root">將 {elmTarget} 的 {`欄位 ${modifyInfo.f} 內容由 ${modifyInfo.b} 變更為 ${modifyInfo.a}。`}</span>;
		}
		catch(e)
		{
			console.log(e);
		}
	}

	return <span className="MuiTypography-body2 MuiTypography-root">{dataValue}</span>;
}
